import StickyTableResponsive from '../../common/StickyTableResponsive';
import SortableTh from '../../common/SortableTh';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTableSort } from '../../../hooks/useTableSort';
import {
  AdminDeactivateModal,
  AdminDeleteModal,
  AdminPeriodFields,
  AdminValidityActions,
  AdminValidityBadge,
  defaultAdminPeriod,
} from './KkpAdminShared';
import AdminBulkPriceAdjustPanel from '../../admin/AdminBulkPriceAdjustPanel';
import AdminFormLabel from '../../admin/AdminFormLabel';
import SearchableSelect from '../../SearchableSelect';
import KkpPlanningMagInlineCell from '../KkpPlanningMagInlineCell';
import { formatEurCell } from '../../../utils/adminBulkPriceAdjust';
import { ADMIN_FIELD_HELP } from '../../../utils/adminHelpContent';
import {
  buildProduitRefLabelMap,
  buildProduitRefOptions,
} from '../../../utils/adminProduitRefOptions';
import clientAnnuaireService from '../../../services/clientAnnuaireService';
import kkpService from '../../../services/kkpService';
import pdcService from '../../../services/pdcService';
import { notify } from '../../common/AppToast';
import { AppButton, AppInput, AppModal, AppSpinner } from '../../ui';

const KLUB_SORT = {
  nom: { type: 'string', getValue: (row) => row.nom },
  frais: { type: 'number', getValue: (row) => row.frais_initial_ht ?? 0 },
  t1_10: { type: 'number', getValue: (row) => row.tarif_1_10 ?? 0 },
  t11_20: { type: 'number', getValue: (row) => row.tarif_11_20 ?? 0 },
  t21_48: { type: 'number', getValue: (row) => row.tarif_21_48 ?? 0 },
  t49: { type: 'number', getValue: (row) => row.tarif_49_plus ?? 0 },
  ordre: { type: 'number', getValue: (row) => row.ordre ?? 0 },
  validite: { type: 'string', getValue: (row) => row.validity_label },
};

const KLUB_TIER_FIELDS = [
  { key: 'tarif_1_10', refKey: 'product_ref_1_10', label: '1–10 sal.', sortKey: 't1_10' },
  { key: 'tarif_11_20', refKey: 'product_ref_11_20', label: '11–20 sal.', sortKey: 't11_20' },
  { key: 'tarif_21_48', refKey: 'product_ref_21_48', label: '21–48 sal.', sortKey: 't21_48' },
  { key: 'tarif_49_plus', refKey: 'product_ref_49_plus', label: '49+ sal.', sortKey: 't49' },
];

const KLUB_PRICE_FIELDS = [
  { key: 'frais_initial_ht', label: 'Frais init. HT' },
  ...KLUB_TIER_FIELDS.map(({ key, label }) => ({ key, label })),
];

const KLUB_PRODUCT_REF_KEYS = [
  ...KLUB_TIER_FIELDS.map((t) => t.refKey),
  'product_ref_frais',
];

const emptyKlubForm = () => ({
  nom: '',
  frais_initial_ht: '',
  tarif_1_10: '',
  tarif_11_20: '',
  tarif_21_48: '',
  tarif_49_plus: '',
  product_ref_1_10: '',
  product_ref_11_20: '',
  product_ref_21_48: '',
  product_ref_49_plus: '',
  product_ref_frais: '',
  ordre: 0,
  valid_from_year: new Date().getFullYear(),
  valid_from_month: new Date().getMonth() + 1,
});

const buildOfferPayload = (base, overrides = {}) => ({
  id: base.id,
  nom: overrides.nom ?? base.nom,
  frais_initial_ht: overrides.frais_initial_ht !== undefined ? overrides.frais_initial_ht : base.frais_initial_ht,
  tarif_1_10: overrides.tarif_1_10 !== undefined ? overrides.tarif_1_10 : base.tarif_1_10,
  tarif_11_20: overrides.tarif_11_20 !== undefined ? overrides.tarif_11_20 : base.tarif_11_20,
  tarif_21_48: overrides.tarif_21_48 !== undefined ? overrides.tarif_21_48 : base.tarif_21_48,
  tarif_49_plus: overrides.tarif_49_plus !== undefined ? overrides.tarif_49_plus : base.tarif_49_plus,
  product_ref_1_10: overrides.product_ref_1_10 !== undefined ? overrides.product_ref_1_10 : (base.product_ref_1_10 ?? null),
  product_ref_11_20: overrides.product_ref_11_20 !== undefined ? overrides.product_ref_11_20 : (base.product_ref_11_20 ?? null),
  product_ref_21_48: overrides.product_ref_21_48 !== undefined ? overrides.product_ref_21_48 : (base.product_ref_21_48 ?? null),
  product_ref_49_plus: overrides.product_ref_49_plus !== undefined ? overrides.product_ref_49_plus : (base.product_ref_49_plus ?? null),
  product_ref_frais: overrides.product_ref_frais !== undefined ? overrides.product_ref_frais : (base.product_ref_frais ?? null),
  ordre: overrides.ordre !== undefined ? overrides.ordre : (base.ordre ?? 0),
  valid_from_year: base.valid_from_year,
  valid_from_month: base.valid_from_month,
});

const KkpAdminKlubSection = () => {
  const [moisOptions, setMoisOptions] = useState([]);
  const [catalogueProduits, setCatalogueProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inlineSaving, setInlineSaving] = useState(false);
  const [klubOffers, setKlubOffers] = useState([]);
  const [pricePushEnabled, setPricePushEnabled] = useState(false);
  const [klubForm, setKlubForm] = useState(emptyKlubForm);
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivatePeriod, setDeactivatePeriod] = useState(defaultAdminPeriod);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { sortedRows: sortedKlubOffers, sort: sortKlub, toggleSort: toggleKlub, getSortIcon: getKlubIcon } = useTableSort(klubOffers, KLUB_SORT);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [offers, planning, tarifsPayload, catalogue] = await Promise.all([
        kkpService.listKlubOffersAdmin(),
        kkpService.listPlanningAdmin(),
        pdcService.listAdminTarifs(),
        clientAnnuaireService.listProduits(),
      ]);
      setKlubOffers(offers);
      setMoisOptions(planning.mois ?? []);
      setPricePushEnabled(Boolean(tarifsPayload.price_push_enabled));
      setCatalogueProduits(catalogue);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const produitRefOptions = useMemo(
    () => buildProduitRefOptions(
      catalogueProduits,
      [
        ...KLUB_PRODUCT_REF_KEYS.map((key) => klubForm[key]),
        ...klubOffers.flatMap((offre) => KLUB_PRODUCT_REF_KEYS.map((key) => offre[key])),
      ],
    ),
    [catalogueProduits, klubForm, klubOffers],
  );
  const produitRefLabelByValue = useMemo(
    () => buildProduitRefLabelMap(produitRefOptions),
    [produitRefOptions],
  );

  const priceByRef = useMemo(() => {
    const map = new Map();
    catalogueProduits.forEach((p) => {
      const ref = String(p.product_ref || '').toUpperCase();
      if (!ref) return;
      if (p.price_ht != null && Number.isFinite(Number(p.price_ht))) {
        map.set(ref, Number(p.price_ht));
      }
    });
    return map;
  }, [catalogueProduits]);

  const wrapSave = async (fn, successMessage) => {
    setSaving(true);
    try {
      await fn();
      if (successMessage) notify.success(successMessage);
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openCreateModal = () => {
    setKlubForm(emptyKlubForm());
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setKlubForm(emptyKlubForm());
  };

  const applyTierRefToForm = (refKey, value) => {
    const tier = KLUB_TIER_FIELDS.find((t) => t.refKey === refKey);
    setKlubForm((f) => {
      const next = { ...f, [refKey]: value };
      if (tier) {
        const price = priceByRef.get(String(value || '').toUpperCase());
        if (price != null) {
          next[tier.key] = String(price);
        }
      }
      return next;
    });
  };

  const submitCreate = () => wrapSave(
    () => kkpService.upsertKlubOfferAdmin({
      nom: klubForm.nom,
      frais_initial_ht: klubForm.frais_initial_ht,
      tarif_1_10: klubForm.tarif_1_10,
      tarif_11_20: klubForm.tarif_11_20,
      tarif_21_48: klubForm.tarif_21_48,
      tarif_49_plus: klubForm.tarif_49_plus,
      product_ref_1_10: klubForm.product_ref_1_10 || null,
      product_ref_11_20: klubForm.product_ref_11_20 || null,
      product_ref_21_48: klubForm.product_ref_21_48 || null,
      product_ref_49_plus: klubForm.product_ref_49_plus || null,
      product_ref_frais: klubForm.product_ref_frais || null,
      ordre: Number(klubForm.ordre) || 0,
      valid_from_year: klubForm.valid_from_year,
      valid_from_month: klubForm.valid_from_month,
    }).then(() => closeModal()),
    'Offre Klub ajoutée',
  );

  const clearInlineEdit = () => {
    setEditingCell(null);
    setEditDraft(null);
  };

  const startInlineEdit = (id, field) => {
    const row = klubOffers.find((item) => item.id === id);
    if (!row) return;
    setEditingCell({ venteId: id, field });
    if (KLUB_PRODUCT_REF_KEYS.includes(field)) {
      setEditDraft(String(row[field] || '').toUpperCase());
    } else {
      setEditDraft(row[field] ?? '');
    }
  };

  const saveInlineEdit = async (draftOverride) => {
    if (!editingCell || inlineSaving) return;
    const { venteId, field } = editingCell;
    const row = klubOffers.find((item) => item.id === venteId);
    if (!row) {
      clearInlineEdit();
      return;
    }

    const draft = draftOverride !== undefined ? draftOverride : editDraft;
    let nextValue = draft;
    const overrides = {};

    if (KLUB_PRODUCT_REF_KEYS.includes(field)) {
      nextValue = String(draft ?? '').trim().toUpperCase() || null;
      const prev = row[field] ? String(row[field]).toUpperCase() : null;
      if (nextValue === prev) {
        clearInlineEdit();
        return;
      }
      overrides[field] = nextValue;
      const tier = KLUB_TIER_FIELDS.find((t) => t.refKey === field);
      if (tier && nextValue) {
        const price = priceByRef.get(nextValue);
        if (price != null) {
          overrides[tier.key] = price;
        }
      }
    } else if (field === 'ordre') {
      nextValue = Number(draft) || 0;
      if (nextValue === (row.ordre ?? 0)) {
        clearInlineEdit();
        return;
      }
      overrides.ordre = nextValue;
    } else if (field === 'frais_initial_ht' || KLUB_TIER_FIELDS.some((t) => t.key === field)) {
      nextValue = draft === '' || draft == null ? null : Number(draft);
      if (Number(nextValue) === Number(row[field])) {
        clearInlineEdit();
        return;
      }
      overrides[field] = nextValue;
    } else if (field === 'nom') {
      nextValue = String(draft ?? '').trim();
      if (!nextValue || nextValue === row.nom) {
        clearInlineEdit();
        return;
      }
      overrides.nom = nextValue;
    } else {
      clearInlineEdit();
      return;
    }

    setInlineSaving(true);
    try {
      await kkpService.upsertKlubOfferAdmin(buildOfferPayload(row, overrides));
      clearInlineEdit();
      notify.success('Modification enregistrée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setInlineSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setSaving(true);
    try {
      await kkpService.deactivateKlubOfferAdmin(deactivateTarget.id, deactivatePeriod);
      setDeactivateTarget(null);
      notify.success('Option désactivée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReactivate = async (id) => {
    setSaving(true);
    try {
      await kkpService.reactivateKlubOfferAdmin(id);
      notify.success('Option réactivée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await kkpService.deleteKlubOfferAdmin(deleteTarget.id);
      setDeleteTarget(null);
      notify.success('Élément supprimé');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const klubBulkItems = useMemo(
    () => klubOffers
      .filter((offre) => offre.validity_status === 'active')
      .flatMap((offre) => KLUB_PRICE_FIELDS
        .filter((field) => offre[field.key] != null && Number(offre[field.key]) > 0)
        .map((field) => ({
          id: `${offre.id}::${field.key}`,
          groupLabel: offre.nom,
          fieldLabel: field.label,
          value: Number(offre[field.key]),
          unit: '€',
        }))),
    [klubOffers],
  );

  const handleKlubBulkApply = async (updates) => {
    setSaving(true);
    try {
      const changesByOffer = {};
      updates.forEach((update) => {
        const [offerId, fieldKey] = update.id.split('::');
        if (!changesByOffer[offerId]) changesByOffer[offerId] = {};
        changesByOffer[offerId][fieldKey] = update.newValue;
      });

      await Promise.all(
        Object.entries(changesByOffer).map(([offerId, fields]) => {
          const offre = klubOffers.find((item) => String(item.id) === offerId);
          if (!offre) return Promise.resolve();
          return kkpService.upsertKlubOfferAdmin(buildOfferPayload(offre, fields));
        }),
      );

      const offers = await kkpService.listKlubOffersAdmin();
      setKlubOffers(offers);
      notify.success(`${updates.length} tarif${updates.length > 1 ? 's' : ''} Klub mis à jour`);
    } catch (err) {
      notify.error(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const renderTierCell = (offre, tier) => {
    const ref = offre[tier.refKey];
    const label = produitRefLabelByValue.get(String(ref || '').toUpperCase()) || ref;
    const amount = formatEurCell(offre[tier.key]);

    return (
      <KkpPlanningMagInlineCell
        venteId={offre.id}
        field={tier.refKey}
        type="searchable-select"
        options={produitRefOptions}
        allowEmpty
        emptyLabel="— Aucune —"
        editingCell={editingCell}
        editDraft={editDraft}
        onEditDraftChange={setEditDraft}
        onStartEdit={startInlineEdit}
        onSave={saveInlineEdit}
        saving={inlineSaving}
        className="text-sm text-end"
      >
        {ref ? (
          <span className="d-inline-flex flex-column align-items-end gap-0">
            <span>{label}</span>
            <span className="text-xs text-secondary-light">{amount}</span>
          </span>
        ) : (
          <span className="text-secondary-light">{amount === '—' ? '—' : amount}</span>
        )}
      </KkpPlanningMagInlineCell>
    );
  };

  if (loading) return <AppSpinner />;

  return (
    <>
      <div className="card admin-data-card">
        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
          <span>Offres Klub ({klubOffers.length})</span>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <AppButton
              size="sm"
              variant={bulkOpen ? 'outline-secondary' : 'danger-600'}
              icon={bulkOpen ? 'mdi:chevron-up' : 'mdi:calculator-variant'}
              disabled={saving || inlineSaving || klubBulkItems.length === 0}
              onClick={() => setBulkOpen((open) => !open)}
            >
              {bulkOpen ? 'Masquer' : 'Augmenter les prix…'}
            </AppButton>
            <AppButton
              size="sm"
              variant="danger-600"
              icon="mdi:plus"
              disabled={saving || inlineSaving}
              onClick={openCreateModal}
            >
              Ajouter
            </AppButton>
          </div>
        </div>

        <AdminBulkPriceAdjustPanel
          hideHeader
          expanded={bulkOpen}
          onExpandedChange={setBulkOpen}
          title="Augmenter les tarifs Klub en groupe"
          description="Frais initiaux et tarifs par tranche de salariés, pour toutes les offres Klub actives."
          items={klubBulkItems}
          onApply={handleKlubBulkApply}
          disabled={saving}
          dolibarrPushEnabled={pricePushEnabled}
        />

        <StickyTableResponsive>
          <table className="table table-hover table-sm mb-0">
            <thead>
              <tr>
                <SortableTh sortKey="nom" label="Offre" sort={sortKlub} onSort={toggleKlub} getSortIcon={getKlubIcon} />
                <th>Réf. frais</th>
                <SortableTh sortKey="frais" label="Frais init." className="text-end" sort={sortKlub} onSort={toggleKlub} getSortIcon={getKlubIcon} />
                {KLUB_TIER_FIELDS.map((tier) => (
                  <SortableTh
                    key={tier.refKey}
                    sortKey={tier.sortKey}
                    label={tier.label}
                    className="text-end"
                    sort={sortKlub}
                    onSort={toggleKlub}
                    getSortIcon={getKlubIcon}
                  />
                ))}
                <SortableTh sortKey="ordre" label="Ordre" className="text-end" sort={sortKlub} onSort={toggleKlub} getSortIcon={getKlubIcon} />
                <SortableTh sortKey="validite" label="Validité" sort={sortKlub} onSort={toggleKlub} getSortIcon={getKlubIcon} />
                <SortableTh label="Actions" className="text-end" sortable={false} />
              </tr>
            </thead>
            <tbody>
              {sortedKlubOffers.map((offre) => (
                <tr key={offre.id} className={offre.validity_status !== 'active' ? 'opacity-50' : ''}>
                  <KkpPlanningMagInlineCell
                    venteId={offre.id}
                    field="nom"
                    type="text"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                  >
                    {offre.nom}
                  </KkpPlanningMagInlineCell>
                  <KkpPlanningMagInlineCell
                    venteId={offre.id}
                    field="product_ref_frais"
                    type="searchable-select"
                    options={produitRefOptions}
                    allowEmpty
                    emptyLabel="— Aucune —"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                    className="text-sm"
                  >
                    {produitRefLabelByValue.get(String(offre.product_ref_frais || '').toUpperCase()) || offre.product_ref_frais || '—'}
                  </KkpPlanningMagInlineCell>
                  <KkpPlanningMagInlineCell
                    venteId={offre.id}
                    field="frais_initial_ht"
                    type="number"
                    inputStep="any"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                    className="text-end"
                  >
                    {formatEurCell(offre.frais_initial_ht)}
                  </KkpPlanningMagInlineCell>
                  {KLUB_TIER_FIELDS.map((tier) => (
                    <React.Fragment key={tier.refKey}>
                      {renderTierCell(offre, tier)}
                    </React.Fragment>
                  ))}
                  <KkpPlanningMagInlineCell
                    venteId={offre.id}
                    field="ordre"
                    type="number"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                    className="text-end"
                  >
                    {offre.ordre ?? 0}
                  </KkpPlanningMagInlineCell>
                  <td>
                    <AdminValidityBadge status={offre.validity_status} />
                    <div className="text-xs text-secondary-light mt-1">{offre.validity_label}</div>
                  </td>
                  <td className="text-end">
                    <AdminValidityActions
                      item={offre}
                      saving={saving || inlineSaving}
                      onDeactivate={(item) => {
                        setDeactivatePeriod(defaultAdminPeriod());
                        setDeactivateTarget({ id: item.id, label: item.nom, kind: 'klub' });
                      }}
                      onReactivate={handleReactivate}
                      onDelete={(item) => setDeleteTarget({ ...item, label: item.nom })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </StickyTableResponsive>
      </div>

      <AppModal
        show={modalOpen}
        title="Ajouter une offre Klub"
        onClose={closeModal}
        size="lg"
        centered
        contentAs="form"
        contentProps={{
          onSubmit: (e) => {
            e.preventDefault();
            submitCreate();
          },
        }}
        footer={(
          <>
            <AppButton type="button" variant="outline-secondary" onClick={closeModal} disabled={saving}>
              Annuler
            </AppButton>
            <AppButton type="submit" variant="danger-600" loading={saving}>
              Ajouter
            </AppButton>
          </>
        )}
      >
        <p className="text-secondary-light small mb-3">
          Liez un produit Dolibarr par tranche de salariés ; le prix catalogue remplit le tarif.
          Les frais d&apos;init restent un montant libre (réf. souvent KLUBINIT).
        </p>
        <div className="row g-3">
          <div className="col-md-6">
            <AdminFormLabel help={ADMIN_FIELD_HELP.klubNom.help} helpExample={ADMIN_FIELD_HELP.klubNom.helpExample} required>
              Nom de l&apos;offre
            </AdminFormLabel>
            <AppInput required value={klubForm.nom} onChange={(e) => setKlubForm((f) => ({ ...f, nom: e.target.value }))} />
          </div>
          <div className="col-md-3">
            <AdminFormLabel help={ADMIN_FIELD_HELP.tarifOrdre.help}>Ordre</AdminFormLabel>
            <AppInput type="number" value={klubForm.ordre} onChange={(e) => setKlubForm((f) => ({ ...f, ordre: e.target.value }))} />
          </div>
          <div className="col-md-6">
            <AdminFormLabel help="Réf. produit Dolibarr pour les frais d'initialisation (ex. KLUBINIT).">Réf. frais</AdminFormLabel>
            <SearchableSelect
              value={klubForm.product_ref_frais}
              onChange={(value) => setKlubForm((f) => ({ ...f, product_ref_frais: value }))}
              options={produitRefOptions}
              allowEmpty
              emptyOptionLabel="— Aucune —"
              placeholder="Produit en vente…"
              emptyLabel="Aucun produit catalogue"
              disabled={saving}
            />
          </div>
          <div className="col-md-3">
            <AdminFormLabel help="Montant HT saisi librement (indépendant du prix catalogue KLUBINIT).">Frais init. HT</AdminFormLabel>
            <AppInput
              type="number"
              step="any"
              value={klubForm.frais_initial_ht}
              onChange={(e) => setKlubForm((f) => ({ ...f, frais_initial_ht: e.target.value }))}
            />
          </div>
          {KLUB_TIER_FIELDS.map((tier) => (
            <div className="col-md-6" key={tier.refKey}>
              <AdminFormLabel help={`Produit Dolibarr pour la tranche ${tier.label}.`}>{tier.label} — réf.</AdminFormLabel>
              <SearchableSelect
                value={klubForm[tier.refKey]}
                onChange={(value) => applyTierRefToForm(tier.refKey, value)}
                options={produitRefOptions}
                allowEmpty
                emptyOptionLabel="— Aucune —"
                placeholder="Produit en vente…"
                emptyLabel="Aucun produit catalogue"
                disabled={saving}
              />
              <div className="mt-2">
                <AdminFormLabel>Montant HT</AdminFormLabel>
                <AppInput
                  type="number"
                  step="any"
                  value={klubForm[tier.key]}
                  onChange={(e) => setKlubForm((f) => ({ ...f, [tier.key]: e.target.value }))}
                  disabled={Boolean(klubForm[tier.refKey])}
                />
              </div>
            </div>
          ))}
          <AdminPeriodFields
            year={klubForm.valid_from_year}
            month={klubForm.valid_from_month}
            moisOptions={moisOptions}
            onYearChange={(year) => setKlubForm((f) => ({ ...f, valid_from_year: year }))}
            onMonthChange={(month) => setKlubForm((f) => ({ ...f, valid_from_month: month }))}
          />
        </div>
      </AppModal>

      <AdminDeactivateModal
        target={deactivateTarget}
        period={deactivatePeriod}
        moisOptions={moisOptions}
        saving={saving}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={handleDeactivate}
        onPeriodChange={setDeactivatePeriod}
      />
      <AdminDeleteModal
        target={deleteTarget}
        contextKey="klub"
        saving={saving}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default KkpAdminKlubSection;
